import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGrade } from "@/contexts/GradeContext";
import { BookOpen, Bell, Check, ChevronDown, LogOut, Menu, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useEffect, useRef, useState } from "react";
import { teacherNotifications } from "@/lib/api";
import TeacherNotifBell from "@/components/TeacherNotifBell";
import TM from "@/components/TM";

function formatCompactTime(date) {
  const h = date.getHours() % 12 || 12;
  const m = String(date.getMinutes()).padStart(2, "0");
  const ampm = date.getHours() < 12 ? "AM" : "PM";
  return { h: String(h), m, ampm };
}

function CompactClock({ isTeacher }) {
  const [parts, setParts] = useState(() => formatCompactTime(new Date()));
  const [colonOn, setColonOn] = useState(true);
  const tickRef = useRef(null);

  useEffect(() => {
    tickRef.current = setInterval(() => {
      setParts(formatCompactTime(new Date()));
      setColonOn(v => !v);
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  return (
    <div
      data-testid="compact-clock"
      className={`hidden sm:flex items-center tabular-nums text-sm font-medium select-none ${
        isTeacher ? "text-slate-500" : "text-slate-500"
      }`}
    >
      <span>{parts.h}</span>
      <span className={colonOn ? "opacity-100" : "opacity-0"} style={{ transition: "none" }}>:</span>
      <span>{parts.m}</span>
      <span className="ml-1 text-xs">{parts.ampm}</span>
    </div>
  );
}

export default function AppHeader() {
  const navigate = useNavigate();
  const { selectedGrade, setSelectedGrade } = useGrade();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [loc.pathname]);

  // Determine role from token
  const studentToken = sessionStorage.getItem("student_token");
  const teacherToken = sessionStorage.getItem("teacher_token");
  const isTeacher = !!teacherToken && loc.pathname.startsWith("/teacher");
  const isStudent = !!studentToken && !isTeacher;

  // Get user info from localStorage
  const learnerName = sessionStorage.getItem("learner_name") || "Student";
  const learnerGrade = parseInt(sessionStorage.getItem("learner_grade") || "5", 10);
  const teacherName = sessionStorage.getItem("teacher_name") || "Teacher";
  const teacherId = sessionStorage.getItem("teacher_id") || "teacher-default";

  // Display name and avatar initial — role-aware
  const displayName = isTeacher ? teacherName : learnerName;

  // For teachers: derive the current grade purely from the URL path.
  // Matches /teacher/cockpit/:grade or /teacher/cockpit/:grade/:subject
  const cockpitGradeFromURL = (() => {
    const m = loc.pathname.match(/^\/teacher\/cockpit\/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  })();

  useEffect(() => {
    let t;
    const tick = async () => {
      if (isTeacher) {
        try {
          const n = await teacherNotifications(teacherId, true);
          setUnread(n.length);
        } catch { /* noop */ }
      }
    };
    tick();
    t = setInterval(tick, 8000);
    return () => clearInterval(t);
  }, [isTeacher, teacherId]);

  const handleLogout = () => {
    if (isTeacher) {
      sessionStorage.removeItem("teacher_token");
      sessionStorage.removeItem("teacher_id");
      sessionStorage.removeItem("teacher_name");
      sessionStorage.removeItem("teacher_email");
      navigate("/teacher/login");
    } else {
      sessionStorage.removeItem("student_token");
      sessionStorage.removeItem("learner_id");
      sessionStorage.removeItem("learner_name");
      sessionStorage.removeItem("learner_grade");
      navigate("/login");
    }
  };

  return (
    <header
      data-testid="app-header"
      className={`sticky top-0 z-30 backdrop-blur-md ${isTeacher ? "bg-white/95 border-b border-slate-200" : "bg-white/80 border-b border-sky-100"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        <Link to={isTeacher ? "/teacher" : "/home"} className="flex items-center gap-2" data-testid="brand-link">
          <div className="w-9 h-9 rounded-xl overflow-hidden border border-slate-200 bg-white flex-shrink-0">
            <img src="/mitraa-logo.webp" alt="Mitraa logo" className="w-full h-full object-contain" />
          </div>
          <div className={`leading-tight ${isTeacher ? "font-teacher" : "font-display"}`}>
            <div className={`font-bold ${isTeacher ? "text-slate-900 text-lg" : "text-slate-800 text-xl"}`}>
              Mitraa<TM />
            </div>
            {!isTeacher && <div className="text-[11px] text-slate-500 -mt-1">A true companion</div>}
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-6">
          {isTeacher ? (
            <>
              <Link to="/teacher" className={`px-3 py-2 rounded-lg text-sm font-medium tap-44 flex items-center ${loc.pathname === "/teacher" || loc.pathname.startsWith("/teacher/cockpit") ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid="nav-teacher-dashboard">Dashboard</Link>
              <Link to="/teacher/heatmap" className={`px-3 py-2 rounded-lg text-sm font-medium tap-44 flex items-center ${loc.pathname === "/teacher/heatmap" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid="nav-teacher-heatmap">Heatmap</Link>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent(
                  loc.pathname.startsWith("/teacher/fm-panel") ? "openFMTestGenerator" : "openTestGenerator"
                ))}
                className={`px-3 py-2 rounded-lg text-sm font-medium tap-44 flex items-center transition-colors ${
                  loc.pathname.startsWith("/teacher/fm-panel")
                    ? "text-fuchsia-700 hover:bg-fuchsia-50"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
                data-testid="nav-teacher-generate-test"
              >
                {loc.pathname.startsWith("/teacher/fm-panel") ? "Generate FM Test" : "Generate Test"}
              </button>
            </>
          ) : (
            <>
              <Link to="/home" className={`px-3 py-2 rounded-full text-sm font-semibold tap-44 flex items-center ${loc.pathname === "/home" ? "bg-sky-100 text-sky-700" : "text-slate-700 hover:bg-sky-50"}`} data-testid="nav-home">
                <BookOpen className="w-4 h-4 mr-1" /> Home
              </Link>
              <Link to="/journey" className={`px-3 py-2 rounded-full text-sm font-semibold tap-44 flex items-center ${loc.pathname === "/journey" ? "bg-sky-100 text-sky-700" : "text-slate-700 hover:bg-sky-50"}`} data-testid="nav-journey">
                <BookOpen className="w-4 h-4 mr-1" /> Journey
              </Link>
            </>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          {/* Compact live clock — hidden on dashboard pages that already show the full date+time */}
          {!(isStudent && loc.pathname === "/home") &&
           !(isTeacher && (loc.pathname === "/teacher" || /^\/teacher\/cockpit\/\d+$/.test(loc.pathname))) && (
            <CompactClock isTeacher={isTeacher} />
          )}

          {isTeacher && (
            <TeacherNotifBell teacherId={teacherId} />
          )}

          {isStudent ? (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200"
              data-testid="grade-badge"
            >
              <span className="text-sm font-semibold text-slate-700">G{learnerGrade}</span>
            </div>
          ) : isTeacher && cockpitGradeFromURL ? (
            <div
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200"
              data-testid="grade-badge"
            >
              <span className="text-sm font-semibold text-slate-700">Grade {cockpitGradeFromURL}</span>
            </div>
          ) : !isTeacher ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors outline-none"
                data-testid="grade-selector"
              >
                <span className="text-sm font-semibold text-slate-700">G{selectedGrade}</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40 rounded-xl">
                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500">Select Grade</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {[1, 2, 3, 4, 5, 6, 7, 8].map((grade) => {
                  const active = grade === selectedGrade;
                  return (
                    <DropdownMenuItem
                      key={grade}
                      onClick={() => setSelectedGrade(grade)}
                      className="flex items-center justify-between cursor-pointer"
                      data-testid={`grade-option-${grade}`}
                    >
                      <span className="text-sm font-medium text-slate-800">Grade {grade}</span>
                      {active && <Check className="w-4 h-4 text-sky-500" />}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

          {/* User profile chip — avatar always visible; name/chevron hidden on mobile */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full bg-sky-50 border border-sky-100 hover:bg-sky-100 transition-colors outline-none"
              data-testid="user-profile-chip"
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isTeacher ? "bg-slate-700 text-white" : "bg-sky-400 text-white"}`}>
                {displayName.slice(0, 1)}
              </div>
              <span className="hidden sm:block text-sm font-semibold text-slate-700">{displayName}</span>
              <ChevronDown className="hidden sm:block w-3.5 h-3.5 text-slate-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-slate-500">
                {isTeacher ? "Teacher Account" : "Student Account"}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="flex items-center gap-2 cursor-pointer text-red-600"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Log Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile hamburger — nav links only, profile chip above handles logout */}
          <button
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg text-slate-600 hover:bg-slate-100 transition"
            onClick={() => setMobileMenuOpen(v => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 shadow-lg" data-testid="mobile-menu-drawer">
          <div className="max-w-7xl mx-auto px-4 py-2 space-y-1">
            {isTeacher ? (
              <>
                <Link to="/teacher" className={`flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium ${loc.pathname === "/teacher" || loc.pathname.startsWith("/teacher/cockpit") ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid="mobile-nav-dashboard">
                  Dashboard
                </Link>
                <Link to="/teacher/heatmap" className={`flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium ${loc.pathname === "/teacher/heatmap" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid="mobile-nav-heatmap">
                  Heatmap
                </Link>
                <button
                  onClick={() => { setMobileMenuOpen(false); window.dispatchEvent(new CustomEvent(loc.pathname.startsWith("/teacher/fm-panel") ? "openFMTestGenerator" : "openTestGenerator")); }}
                  className="w-full text-left flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
                  data-testid="mobile-nav-generate-test"
                >
                  {loc.pathname.startsWith("/teacher/fm-panel") ? "Generate FM Test" : "Generate Test"}
                </button>
              </>
            ) : (
              <>
                <Link to="/home" className={`flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-semibold ${loc.pathname === "/home" ? "bg-sky-100 text-sky-700" : "text-slate-700 hover:bg-sky-50"}`} data-testid="mobile-nav-home">
                  <BookOpen className="w-4 h-4" /> Home
                </Link>
                <Link to="/journey" className={`flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-semibold ${loc.pathname === "/journey" ? "bg-sky-100 text-sky-700" : "text-slate-700 hover:bg-sky-50"}`} data-testid="mobile-nav-journey">
                  <BookOpen className="w-4 h-4" /> Journey
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
